import { T } from '@bangle.io/mini-js-utils';
import { narrow } from './common';

// Workspace Commands
export const wsCommands = narrow([
  {
    id: 'command::ws:create-note',
    title: 'New Note',
    omniSearch: false,
    dependencies: {
      services: ['fileSystem', 'navigation'],
      commands: ['command::ui:rename-note-dialog', 'command::ui:focus-editor'],
    },
    args: {
      wsPath: T.String,
      // whether to navigate to the newly created note
      navigate: T.Optional(T.Boolean),
      // open rename dialog after create so the user can name it immediately
      promptRename: T.Optional(T.Boolean),
    },
  },
  {
    id: 'command::ws:quick-new-note',
    title: 'Quick New Note',
    keywords: ['new', 'create', 'note', 'quick', 'untitled'],
    dependencies: {
      services: ['workspaceState'],
      commands: ['command::ws:new-note-from-input'],
    },
    omniSearch: 'workspace',
    autoFocusEditor: false,
    args: {
      pathPrefix: T.Optional(T.String),
    },
  },
  {
    id: 'command::ws:new-note-from-input',
    title: 'New Note',
    omniSearch: false,
    dependencies: {
      services: ['workspaceState', 'fileSystem', 'navigation'],
      commands: ['command::ws:create-note'],
    },
    args: {
      inputPath: T.String,
      promptRename: T.Optional(T.Boolean),
    },
  },

  {
    id: 'command::ws:create-directory',
    title: 'Create Directory',
    omniSearch: false,
    dependencies: {
      services: ['fileSystem'],
    },
    args: {
      dirWsPath: T.String,
    },
  },
  {
    id: 'command::ws:rename-directory',
    title: 'Rename Directory',
    omniSearch: false,
    dependencies: {
      services: [
        'editorEngine',
        'fileSystem',
        'userActivityService',
        'workspaceOps',
        'workspaceState',
      ],
    },
    args: {
      oldDirWsPath: T.String,
      newDirWsPath: T.String,
    },
  },
  {
    id: 'command::ws:delete-directory',
    title: 'Delete Directory',
    omniSearch: false,
    dependencies: {
      services: ['fileSystem', 'navigation', 'workspaceOps', 'workspaceState'],
    },
    args: {
      dirWsPath: T.String,
    },
  },

  {
    id: 'command::ws:go-workspace',
    title: 'Go to Workspace',
    omniSearch: false,
    dependencies: { services: ['navigation'] },
    args: {
      wsName: T.String,
    },
  },
  {
    id: 'command::ws:go-ws-path',
    title: 'Go to wsPath',
    omniSearch: false,
    dependencies: {
      services: ['navigation'],
    },
    args: {
      wsPath: T.String,
    },
  },
  {
    id: 'command::ws:delete-workspace',
    title: 'Delete Workspace',
    omniSearch: false,
    dependencies: { services: ['workspaceOps', 'navigation'] },
    args: {
      wsName: T.String,
    },
  },
  {
    id: 'command::ws:delete-ws-path',
    title: 'Delete Note',
    omniSearch: false,
    dependencies: { services: ['fileSystem', 'navigation', 'workspaceOps'] },
    args: {
      wsPath: T.String,
    },
  },
  {
    id: 'command::ws:delete-ws-paths',
    title: 'Delete Files',
    omniSearch: false,
    dependencies: { services: ['fileSystem', 'navigation', 'workspaceOps'] },
    args: {
      wsPaths: T.Array(T.String),
    },
  },
  {
    id: 'command::ws:rename-ws-path',
    title: 'Rename Note',
    omniSearch: false,
    dependencies: {
      services: [
        'editorEngine',
        'fileSystem',
        'userActivityService',
        'workspaceOps',
      ],
    },
    args: {
      wsPath: T.String,
      newWsPath: T.String,
    },
  },
  {
    id: 'command::ws:move-ws-path',
    title: 'Move Note',
    omniSearch: false,
    dependencies: {
      services: [
        'editorEngine',
        'fileSystem',
        'userActivityService',
        'workspaceOps',
      ],
    },
    args: {
      wsPath: T.String,
      destDirWsPath: T.String,
    },
  },
  {
    id: 'command::ws:move-ws-paths',
    title: 'Move Notes',
    omniSearch: false,
    dependencies: {
      services: [
        'editorEngine',
        'fileSystem',
        'userActivityService',
        'workspaceOps',
      ],
    },
    args: {
      wsPaths: T.Array(T.String),
      destDirWsPath: T.String,
    },
  },
  {
    id: 'command::ws:go-ws-home',
    title: 'Go to Workspace Home',
    keywords: ['home', 'workspace', 'go'],
    omniSearch: 'workspace',
    dependencies: { services: ['navigation'] },
    args: null,
  },
  {
    id: 'command::ws:go-graph',
    title: 'Open Graph View',
    keywords: ['graph', 'links', 'network', 'workspace', 'map'],
    omniSearch: 'workspace',
    dependencies: { services: ['navigation'] },
    args: null,
  },
  {
    id: 'command::ws:refresh-file-tree',
    title: 'Refresh Files',
    keywords: ['refresh', 'reload', 'rescan', 'files', 'file tree'],
    omniSearch: 'workspace',
    dependencies: { services: ['fileSystem'] },
    args: null,
  },
  {
    id: 'command::ws:export-workspace',
    title: 'Export Workspace',
    keywords: ['export', 'backup', 'zip', 'download', 'markdown'],
    omniSearch: 'workspace',
    dependencies: { services: ['workspaceState', 'fileSystem'] },
    args: {
      wsName: T.Optional(T.String),
    },
  },
  {
    id: 'command::ws:clone-note',
    title: 'Clone Note',
    omniSearch: 'note',
    keywords: ['clone', 'duplicate', 'copy'],
    dependencies: { services: ['workspaceState', 'fileSystem', 'navigation'] },
    args: {
      wsPath: T.Optional(T.String),
    },
  },
  {
    id: 'command::ws:clone-directory',
    title: 'Clone Folder',
    omniSearch: false,
    keywords: ['clone', 'duplicate', 'copy', 'folder'],
    dependencies: { services: ['workspaceState', 'fileSystem'] },
    args: {
      dirWsPath: T.String,
    },
  },
  {
    id: 'command::ws:recover-note-snapshot',
    title: 'Recover Note Snapshot',
    omniSearch: false,
    dependencies: {
      services: ['noteSnapshot', 'fileSystem', 'navigation'],
    },
    args: {
      snapshotId: T.String,
    },
  },
  {
    id: 'command::ws:daily-note',
    title: 'Open Daily Note',
    keywords: ['daily', 'note', 'today', 'journal'],
    omniSearch: 'workspace',
    dependencies: {
      services: ['workspaceState', 'fileSystem', 'navigation'],
      commands: ['command::ws:create-note'],
    },
    args: {
      date: T.Optional(T.Number),
    },
  },
  {
    id: 'command::workspace:toggle-star',
    title: 'Toggle Star for Current Note',
    keywords: ['star', 'unstar', 'favorite', 'note', 'toggle', 'workspace'],
    dependencies: {
      services: ['workspaceState', 'userActivityService'],
    },
    omniSearch: 'note',
    keybindings: ['ctrl', 'shift', 's'],
    args: {
      wsPath: T.Optional(T.String),
    },
  },
]);
